import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Space, Button, Input, Tooltip, Pagination, Empty, Spin, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PaperClipOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import { isAdmin } from '../../../utils/userRoles';
import EncyclopediaDeleteModal from '../delete/EncyclopediaDeleteModal';
import './EncyclopediaList.css';

const { Title, Paragraph, Text } = Typography;

const DEFAULT_PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 400;

export default function EncyclopediaList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authReducer);
  const canManage = isAdmin(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchItems = useCallback(
    async (page, limit, search) => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (search && search.trim()) params.search = search.trim();
        const res = await axios.get('/encyclopedia', { params });
        setItems(res.data?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          current: res.data?.page ?? page,
          pageSize: res.data?.limit ?? limit,
          total: res.data?.total ?? 0,
        }));
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to load encyclopedia';
        dispatch(setMessage({ message: msg, type: 'error' }));
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const refetch = useCallback(() => {
    fetchItems(pagination.current, pagination.pageSize, searchApplied);
  }, [fetchItems, pagination.current, pagination.pageSize, searchApplied]);

  useEffect(() => {
    fetchItems(pagination.current, pagination.pageSize, searchApplied);
  }, [pagination.current, pagination.pageSize, searchApplied, fetchItems]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchApplied(searchInput.trim());
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/encyclopedia/${entryToDelete._id}`);
      dispatch(setMessage({ message: 'Entry deleted', type: 'success' }));
      setEntryToDelete(null);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setDeleteLoading(false);
    }
  };

  const attachmentCount = (row) => (Array.isArray(row.attachments) ? row.attachments.length : 0);

  return (
    <div className="encyclopedia-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="encyclopedia-header">
          <div className="encyclopedia-header-title-row">
            <div className="encyclopedia-heading-block">
              <Title level={3} style={{ margin: 0 }}>
                Encyclopedia
              </Title>
              <Text className="encyclopedia-subtitle">Browse internal knowledge and quick references</Text>
            </div>
            {canManage && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/encyclopedia/new')}
                className="app-btn-primary"
              >
                New entry
              </Button>
            )}
          </div>
          <Input
            placeholder="Search by name or description"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            allowClear
            className="encyclopedia-search-input"
            prefix={<SearchOutlined />}
          />
          <div className="encyclopedia-results-row">
            <Text type="secondary">
              {pagination.total} result{pagination.total !== 1 ? 's' : ''}
            </Text>
            {searchApplied && <Tag color="blue">Search: {searchApplied}</Tag>}
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : items.length === 0 ? (
          <Empty description="No entries yet" />
        ) : (
          <Row gutter={[16, 16]}>
            {items.map((row) => (
              <Col key={row._id} xs={24} sm={8} md={8} xl={8}>
                <Card
                  className="encyclopedia-card"
                  title={row.name || 'Untitled'}
                  hoverable
                  onClick={() => navigate(`/encyclopedia/${row._id}`)}
                >
                  <div className="encyclopedia-card-body">
                    <Paragraph ellipsis={{ rows: 3 }} className="encyclopedia-card-description">
                      {row.description || 'No description provided'}
                    </Paragraph>
                  </div>
                  <div className="encyclopedia-card-footer">
                    <div className="encyclopedia-card-meta">
                      <PaperClipOutlined /> {attachmentCount(row)} attachment{attachmentCount(row) !== 1 ? 's' : ''}
                    </div>
                    <div className="encyclopedia-card-actions">
                      
                      {canManage && (
                        <>
                          <Tooltip title="View">
                            <Button
                              type="default"
                              size="small"
                              icon={<EyeOutlined />}
                              className="encyclopedia-card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/encyclopedia/${row._id}`);
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Edit">
                            <Button
                              type="default"
                              size="small"
                              icon={<EditOutlined />}
                              className="encyclopedia-card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/encyclopedia/${row._id}/edit`);
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Delete">
                            <Button
                              type="default"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              className="encyclopedia-card-action-btn app-btn-delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEntryToDelete(row);
                              }}
                            />
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {pagination.total > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger
              pageSizeOptions={['12', '24', '48']}
              showTotal={(t) => `Total ${t} entries`}
              onChange={(page, pageSize) => {
                setPagination((prev) => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize || prev.pageSize,
                }));
              }}
            />
          </div>
        )}

        {canManage && (
          <EncyclopediaDeleteModal
            open={entryToDelete}
            onCancel={() => setEntryToDelete(null)}
            onConfirm={handleDeleteConfirm}
            entry={entryToDelete}
            loading={deleteLoading}
          />
        )}
      </Space>
    </div>
  );
}
